import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";

/**
 * Annotation overlay canvas.
 * - read-only when interactive=false
 * - drawable when interactive=true
 *
 * Strokes are stored with normalized coordinates [0,1] so they scale across
 * different canvas sizes (smaller teacher preview vs full-screen smartboard).
 *
 * Performance notes (this file is performance-critical on low-end Android
 * interactive panels):
 *   • Committed strokes are painted onto a BACKGROUND canvas only when the
 *     `strokes` prop actually changes — not on every pointer move.
 *   • The in-progress stroke is painted onto a FOREGROUND canvas
 *     INCREMENTALLY: we draw just the new line segment per pointer event
 *     using `lineTo` + `stroke()`. No `clearRect`, no React re-render.
 *   • On pointer up, the foreground is cleared and the finished stroke is
 *     committed via `onAddStroke` — which appends it to the `strokes` prop
 *     on the next render and the background re-paints once.
 *
 * Props:
 *  - strokes:     stroke[]
 *  - interactive: boolean
 *  - tool:        'off' | 'pen' | 'highlight'
 *  - color:       hex color
 *  - onAddStroke: (stroke) => void
 */
export const AnnotationCanvas = ({
    strokes = [],
    interactive = false,
    tool = "off",
    color = "#2E8E9A",
    onAddStroke,
}) => {
    const wrapperRef = useRef(null);
    const bgCanvasRef = useRef(null); // committed strokes
    const fgCanvasRef = useRef(null); // in-progress stroke
    const sizeRef = useRef({ w: 0, h: 0, dpr: 1 });

    // In-progress stroke is tracked imperatively (no re-renders mid-draw)
    const drawingRef = useRef(false);
    const draftRef = useRef(null); // { points, color, mode }
    const lastPointRef = useRef(null); // [xPx, yPx] in CSS pixels

    /* --- size both canvases to the wrapper, accounting for DPR --- */
    const resizeCanvases = useCallback(() => {
        const wrap = wrapperRef.current;
        const bg = bgCanvasRef.current;
        const fg = fgCanvasRef.current;
        if (!wrap || !bg || !fg) return;

        const w = wrap.clientWidth;
        const h = wrap.clientHeight;
        if (w === 0 || h === 0) return;
        const dpr = window.devicePixelRatio || 1;
        sizeRef.current = { w, h, dpr };

        for (const c of [bg, fg]) {
            c.width = Math.round(w * dpr);
            c.height = Math.round(h * dpr);
            c.style.width = w + "px";
            c.style.height = h + "px";
            const ctx = c.getContext("2d");
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.scale(dpr, dpr);
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
        }
    }, []);

    /* --- paint all committed strokes to the background canvas --- */
    const paintCommitted = useCallback(() => {
        const c = bgCanvasRef.current;
        if (!c) return;
        const { w, h } = sizeRef.current;
        if (w === 0 || h === 0) return;
        const ctx = c.getContext("2d");
        ctx.clearRect(0, 0, w, h);
        for (const s of strokes) {
            if (!s?.points || s.points.length === 0) continue;
            ctx.strokeStyle = s.color;
            ctx.globalAlpha = s.mode === "highlight" ? 0.35 : 1;
            ctx.lineWidth = s.mode === "highlight" ? 18 : 3.5;
            ctx.beginPath();
            const [x0, y0] = s.points[0];
            ctx.moveTo(x0 * w, y0 * h);
            if (s.points.length === 1) {
                ctx.lineTo(x0 * w + 0.5, y0 * h + 0.5);
            } else {
                for (let i = 1; i < s.points.length; i++) {
                    const [x, y] = s.points[i];
                    ctx.lineTo(x * w, y * h);
                }
            }
            ctx.stroke();
        }
        ctx.globalAlpha = 1;
    }, [strokes]);

    useLayoutEffect(() => {
        resizeCanvases();
        paintCommitted();
    }, [resizeCanvases, paintCommitted]);

    /* --- track size changes (e.g., entering fullscreen) --- */
    useEffect(() => {
        const wrap = wrapperRef.current;
        if (!wrap) return;
        const ro = new ResizeObserver(() => {
            resizeCanvases();
            paintCommitted();
        });
        ro.observe(wrap);
        return () => ro.disconnect();
    }, [resizeCanvases, paintCommitted]);

    /* --- pointer handlers --- */
    const getPx = (e) => {
        const rect = wrapperRef.current.getBoundingClientRect();
        return [e.clientX - rect.left, e.clientY - rect.top];
    };

    const setupFgStyle = (ctx, mode, strokeColor) => {
        ctx.strokeStyle = strokeColor;
        ctx.globalAlpha = mode === "highlight" ? 0.35 : 1;
        ctx.lineWidth = mode === "highlight" ? 18 : 3.5;
    };

    const pointerDown = (e) => {
        if (!interactive || tool === "off") return;
        e.preventDefault();
        e.target.setPointerCapture?.(e.pointerId);
        const [px, py] = getPx(e);
        drawingRef.current = true;
        draftRef.current = {
            id: `s-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            points: [[px / sizeRef.current.w, py / sizeRef.current.h]],
            color,
            mode: tool,
        };
        lastPointRef.current = [px, py];

        const ctx = fgCanvasRef.current.getContext("2d");
        setupFgStyle(ctx, tool, color);
        // tiny dot for a tap
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(px + 0.5, py + 0.5);
        ctx.stroke();
    };

    const pointerMove = (e) => {
        if (!drawingRef.current || !draftRef.current) return;
        // throttle to actual movement deltas — multiple coalesced events
        // get batched by the browser already, but we still avoid drawing
        // sub-pixel duplicates.
        const events =
            typeof e.getCoalescedEvents === "function"
                ? e.getCoalescedEvents()
                : [e];
        const ctx = fgCanvasRef.current.getContext("2d");
        setupFgStyle(ctx, draftRef.current.mode, draftRef.current.color);
        const { w, h } = sizeRef.current;
        const rect = wrapperRef.current.getBoundingClientRect();

        ctx.beginPath();
        const [lx, ly] = lastPointRef.current;
        ctx.moveTo(lx, ly);
        let nx = lx;
        let ny = ly;
        for (const ev of events) {
            nx = ev.clientX - rect.left;
            ny = ev.clientY - rect.top;
            ctx.lineTo(nx, ny);
            draftRef.current.points.push([nx / w, ny / h]);
        }
        ctx.stroke();
        lastPointRef.current = [nx, ny];
    };

    const pointerUp = () => {
        if (!drawingRef.current) return;
        drawingRef.current = false;
        const draft = draftRef.current;
        draftRef.current = null;
        lastPointRef.current = null;
        // Clear foreground; the committed background will repaint once
        // the parent re-renders with the new strokes prop.
        const fg = fgCanvasRef.current;
        if (fg) {
            const ctx = fg.getContext("2d");
            const { w, h } = sizeRef.current;
            ctx.clearRect(0, 0, w, h);
        }
        if (draft && draft.points.length > 0) {
            onAddStroke?.(draft);
        }
    };

    return (
        <div
            ref={wrapperRef}
            className="absolute inset-0"
            style={{
                pointerEvents: interactive && tool !== "off" ? "auto" : "none",
                cursor:
                    interactive && tool !== "off" ? "crosshair" : "default",
                touchAction: "none",
            }}
            onPointerDown={pointerDown}
            onPointerMove={pointerMove}
            onPointerUp={pointerUp}
            onPointerCancel={pointerUp}
            onPointerLeave={pointerUp}
            data-testid={
                interactive
                    ? "annotation-canvas-interactive"
                    : "annotation-canvas-readonly"
            }
        >
            <canvas ref={bgCanvasRef} className="absolute inset-0" />
            <canvas
                ref={fgCanvasRef}
                className="absolute inset-0 pointer-events-none"
            />
        </div>
    );
};

export default AnnotationCanvas;
